/**
 * "Références" — visible source citations for every quantitative claim
 * on the landing page. Required by [[marketing-fact-check-policy]]:
 *
 * - DGCCRF (French consumer protection): unsubstantiated quantitative
 *   marketing claims are penalised (fines from €75k for B2B SMEs).
 * - Levier IA's "AI without the hype" positioning: getting caught with
 *   recycled / invented stats torpedoes credibility instantly.
 *
 * Each entry maps to a numbered <sup>[N]</sup> reference in WhyNow.tsx
 * (and any other section that cites). Update both this file AND the
 * caller when a number changes.
 */

interface Source {
  ref: number;
  claim: string;
  description: string;
  href: string;
  fetched: string;
}

const SOURCES: Source[] = [
  {
    ref: 1,
    claim: "ChatGPT — 800 M utilisateurs actifs/semaine (oct. 2025)",
    description:
      "Business Insider, OpenAI Dev Day, oct. 2025. Trace de progression : Reuters (fév. 2025) et TechCrunch (fév. 2025) documentent le passage à 400 M.",
    href: "https://www.businessinsider.com/chatgpt-users-openai-sam-altman-devday-llm-artificial-intelligence-2025-10",
    fetched: "2026-05-25",
  },
  {
    ref: 2,
    claim: "Crawlers IA — forte hausse documentée du trafic (2025)",
    description:
      "Cloudflare Radar — AI Insights 2025. Aucun multiple officiel publié ; la formulation « forte hausse » reflète l'absence de chiffre consensus.",
    href: "https://radar.cloudflare.com/ai-insights",
    fetched: "2026-05-25",
  },
  {
    ref: 3,
    claim: "SBF 120 — < 5 % publient un fichier llms.txt (mars 2026)",
    description:
      "Observation propriétaire Levier IA, échantillon mars 2026. Cohérent avec les crawls publics 2025 montrant 1–10 % d'adoption selon l'échantillon (chris-green.net, Search Engine Journal).",
    href: "https://www.searchenginejournal.com/llms-txt-shows-no-clear-effect-on-ai-citations-based-on-300k-domains/561542/",
    fetched: "2026-05-25",
  },
  {
    ref: 4,
    claim: "Refonte web Paris — 8–15 k€ HT (données marché 2025-2026)",
    description:
      "seo.fr et Codeur, données marché 2025-2026. TJM moyen consultant SEO France : 700–1 200 €/jour, refonte complète 4–10 jours facturés.",
    href: "https://www.codeur.com/consultant/seo/tarif",
    fetched: "2026-05-25",
  },
];

export function Sources() {
  return (
    <section
      id="sources"
      className="py-16 px-6 bg-slate-950 border-t border-slate-800"
      aria-label="Références et sources"
    >
      <div className="max-w-4xl mx-auto">
        <p className="text-xs font-mono uppercase tracking-[0.18em] text-slate-500 mb-6">
          Références
        </p>

        <ol className="space-y-5">
          {SOURCES.map((s) => (
            <li
              key={s.ref}
              id={`ref-${s.ref}`}
              className="grid grid-cols-[auto_1fr] gap-x-4 scroll-mt-24"
            >
              <span className="font-mono text-sm text-blue-400 tabular-nums">
                [{s.ref}]
              </span>
              <div className="min-w-0">
                <p className="text-sm text-slate-300 font-medium leading-relaxed">
                  {s.claim}
                </p>
                <p className="mt-1 text-xs text-slate-500 leading-relaxed">
                  {s.description}
                </p>
                <a
                  href={s.href}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="mt-1 inline-block font-mono text-[11px] text-blue-400 hover:text-blue-300 underline decoration-blue-500/40 hover:decoration-blue-300 transition-colors break-all"
                >
                  {s.href}
                </a>
                <span className="ml-3 font-mono text-[10px] text-slate-600 tabular-nums">
                  consulté {s.fetched}
                </span>
              </div>
            </li>
          ))}
        </ol>

        <p className="mt-10 text-[11px] text-slate-600 leading-relaxed max-w-2xl">
          Chaque chiffre quantitatif affiché sur cette page renvoie à sa source
          datée et publiquement vérifiable. Les claims non chiffrés ou
          d'observation propriétaire sont indiqués comme tels. Mise à jour
          continue.
        </p>
      </div>
    </section>
  );
}
